import type { ReactNode } from "react";
import * as AntdIcons from "@ant-design/icons";
import type { ProColumns } from "@ant-design/pro-components";
import type { FieldPropsConfig, TableColumnConfig } from "@mijalowcode/types";
import {
  ProTableSlotCell,
  type ProTableAreaNodesResolver,
} from "./slot-cell";

function isBindValue(value: unknown): value is {
  __bind: {
    fallback?: unknown;
  };
} {
  return (
    Boolean(value) &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    typeof (value as Record<string, unknown>).__bind === "object"
  );
}

function renderFieldPropsPrefixSuffix(
  fieldProps: FieldPropsConfig | undefined,
) {
  if (!fieldProps) return undefined;

  const result: FieldPropsConfig = { ...fieldProps };

  if (fieldProps.prefixType === "icon" && fieldProps.prefixIcon) {
    const icons = AntdIcons as unknown as Record<
      string,
      React.ComponentType<unknown>
    >;
    const IconComponent = icons[fieldProps.prefixIcon];
    result.prefix = IconComponent ? <IconComponent /> : undefined;
  } else if (fieldProps.prefixText) {
    result.prefix = fieldProps.prefixText as React.ReactNode;
  }

  if (fieldProps.suffixType === "icon" && fieldProps.suffixIcon) {
    const icons = AntdIcons as unknown as Record<
      string,
      React.ComponentType<unknown>
    >;
    const IconComponent = icons[fieldProps.suffixIcon];
    result.suffix = IconComponent ? <IconComponent /> : undefined;
  } else if (fieldProps.suffixText) {
    result.suffix = fieldProps.suffixText as React.ReactNode;
  }

  return result;
}

function resolveColumnValueEnum(
  col: TableColumnConfig,
): ProColumns["valueEnum"] {
  if (isBindValue(col.valueEnum)) {
    return col.valueEnum.__bind.fallback as ProColumns["valueEnum"];
  }
  return col.valueEnum as ProColumns["valueEnum"];
}

export function resolveColumnSlotAreaNames(col: TableColumnConfig) {
  const areaNames: string[] = [];
  const columnId = String(col.columnId || "").trim();
  if (columnId) {
    areaNames.push(`columnId:${columnId}`);
  }
  return areaNames;
}

// 将低代码列配置转换为 ProTable 列配置，并按约定挂载列级插槽渲染
export function convertColumns(
  columns: TableColumnConfig[] = [],
  getAreaNodes?: ProTableAreaNodesResolver,
  areaMap: Record<string, ReactNode[]> = {},
  hasExternalRenderSlot = false,
  runtimeContext?: Record<string, unknown>,
): ProColumns[] {
  return columns.map((col) => {
    const extendedCol = col as TableColumnConfig & {
      slotName?: string;
      request?: ProColumns["request"];
    };
    const areaNames = resolveColumnSlotAreaNames(col);
    const hasSchemaSlot = areaNames.some(
      (areaName) => areaMap[areaName]?.length,
    );

    const proColumn: ProColumns = {
      key: col.key || col.dataIndex,
      title: col.title,
      dataIndex: col.dataIndex,
      width: col.width,
      align: col.align,
      fixed: col.fixed || undefined,
      ellipsis: col.ellipsis,
      copyable: col.copyable,
      sorter: col.sorter,
      valueType: col.valueType as any,
      valueEnum: resolveColumnValueEnum(col),
      request: extendedCol.request,
      hideInSearch: col.hideInSearch,
      hideInTable: col.hideInTable,
      formItemProps: col.formItemProps,
      fieldProps: renderFieldPropsPrefixSuffix(col.fieldProps) as any,
    };

    if (!areaNames.length || (!hasExternalRenderSlot && !hasSchemaSlot)) {
      return proColumn;
    }

    proColumn.render = (_, record: Record<string, unknown>, index: number) => {
      const rawValue = record[col.dataIndex];
      const valueEnum = resolveColumnValueEnum(col);
      return (
        <ProTableSlotCell
          areaNames={areaNames}
          column={col}
          getAreaNodes={getAreaNodes}
          index={index}
          record={record}
          runtimeContext={runtimeContext}
          value={rawValue}
          valueEnum={valueEnum}
        />
      );
    };

    return proColumn;
  });
}
